'use client';
import { useConfig } from '@lib/context/config-context';
import { getMedusaClient } from '@lib/config';
import * as orderConfirmHandle from '@lib/data/order-confirm-handle';

/**
 * Hook that provides API functions and services with config automatically injected.
 * Use this in client components instead of importing from config or data files directly.
 *
 * Matches the pattern used in redemption-ui for consistency.
 *
 * @example
 * const api = useApi();
 * const medusaClient = api.getMedusaClient();
 * const config = api.config;
 */
export function useApi() {
  const config = useConfig();

  return {
    // Expose config for components that need direct access
    config,

    // Medusa client with runtime API key and CLIENT backend URL (for browser requests)
    getMedusaClient: () => getMedusaClient(config.CLIENT_API_KEY, config.MEDUSA_BACKEND_URL_CLIENT),

    // Helper getters for commonly used config values
    get medusaBackendUrl() {
      return config.MEDUSA_BACKEND_URL_CLIENT;
    },

    get stripeKey() {
      return config.STRIPE_KEY;
    },

    get googleApiKey() {
      return config.GOOGLE_API_KEY;
    },

    get googleCaptchaKey() {
      return config.GOOGLE_CAPTCHA_KEY;
    },

    get redemptionServiceUrl() {
      return config.REDEMPTION_SERVICE_URL;
    },

    get redemptionUIUrl() {
      return config.REDEMPTION_UI_URL;
    },

    get searchServiceUrl() {
      return config.SEARCH_SERVICE_URL;
    },

    get baseUrl() {
      return config.API_BASE_URL;
    },

    get paypalClientId() {
      return config.PAYPAL_CLIENT_ID;
    },

    get gtmKey() {
      return config.GTM_KEY;
    },

    get fiservPaymentJsUrl() {
      return config.FISERV_PAYMENTJS_URL;
    },

    get enableScheduleDelivery() {
      return config.ENABLE_SCHEDULE_DELIVERY === 'true';
    },

    get disableLoginSignup() {
      return config.DISABLE_LOGIN_SIGNUP === 'true';
    },

    get stripeEnableOtherPay() {
      return config.STRIPE_ENABLE_OTHER_PAY.toString() === 'true' ;
    },

    get enableGoogleCaptcha() {
      return config.ENABLE_GOOGLE_CAPTCHA === 'true';
    },

    get variantCardConfig() {
      return config.VARIANT_CARD_CONFIG;
    },

    // API Functions with config auto-injected
    verifyRecaptchaToken: (token: string, action: string) =>
      orderConfirmHandle.verifyRecaptchaTokenWithConfig(token, action, config),
  };
}
